import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ValidatorService } from '../../core/services/validator.service';
import { combineLatest, Observable, withLatestFrom } from 'rxjs';
import { DatePickerComponent, DatepickerLanguage, IconRadioButtonOption } from 'dap-ui-library';
import { map, startWith } from 'rxjs/operators';
import { GenderOrPrenatal } from '../../core/api';
import { TranslateService } from '@ngx-translate/core';
import { PersonService } from '../../core/services/person.service';
import { Store } from '@ngrx/store';
import { createGetPersonOrUndefined } from '../../core/states/offer/offer.selector';
import { PersonDataFormGroup } from './person-data.forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isNotNullOrUndefined } from '../../core/utils/typeUtils';
import dayjs from 'dayjs';
import { MAX_PRENATAL_DATE_OF_BIRTH, MIN_DATE_OF_BIRTH } from '../../core/services/date.service';

interface DatePickerConfig {
  minDate: Date;
  maxDate: Date;
  currentLanguage: DatepickerLanguage;
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-person-data',
  templateUrl: './person-data.component.html',
  styleUrls: ['./person-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonDataComponent implements OnInit, AfterViewInit {
  @Input() public personFormGroup: PersonDataFormGroup;
  @Input() public index: number;
  @Input() public showHeader = false;
  @Input() public showNameField = true;
  @Input() public validateForm: boolean;
  @Input() public scrollIntoView?: boolean = true;
  @Output() public remove = new EventEmitter();

  public hideDateValidation: boolean;

  @ViewChild('dateOfBirthInputField')
  private dateOfBirthInputField: DatePickerComponent;
  public genderOptions$: Observable<IconRadioButtonOption[]>;
  public datePickerConfig$: Observable<DatePickerConfig>;

  constructor(
    private translateService: TranslateService,
    public validatorService: ValidatorService,
    private personService: PersonService,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    this.datePickerConfig$ = this.getDatePickerConfig$();
    this.genderOptions$ = this.getGenderIconOptions$();
  }

  public ngAfterViewInit(): void {
    if (this.scrollIntoView) {
      this.dateOfBirthInputField.elementRef.nativeElement.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }

  public getFirstNameOrFallback(): string {
    return this.personService.getFirstNameOrFallback(
      this.personFormGroup.controls.firstName.value ?? undefined,
      this.index + 1,
    );
  }

  public isPrenatalSelected(): boolean {
    return this.personFormGroup.value.gender === GenderOrPrenatal.Prenatal;
  }

  private getDatePickerConfig$(): Observable<DatePickerConfig> {
    return combineLatest([this.getCurrentLanguage$(), this.personFormGroup.controls.gender.valueChanges]).pipe(
      untilDestroyed(this),
      map(([currentLanguage, gender]) => {
        const isUnborn = isNotNullOrUndefined(gender) && gender === 'PRENATAL';
        return {
          minDate: isUnborn ? dayjs().toDate() : MIN_DATE_OF_BIRTH.toDate(),
          maxDate: isUnborn ? MAX_PRENATAL_DATE_OF_BIRTH.toDate() : dayjs().toDate(),
          currentLanguage,
        };
      }),
      startWith({
        minDate: MIN_DATE_OF_BIRTH.toDate(),
        maxDate: dayjs().toDate(),
        currentLanguage: this.translateService.currentLang,
      } as DatePickerConfig),
    );
  }

  private getCurrentLanguage$(): Observable<DatepickerLanguage> {
    return this.translateService.onLangChange.pipe(
      untilDestroyed(this),
      startWith(this.translateService.currentLang),
      map((language) => {
        switch (language) {
          case 'fr':
            return 'fr';
          case 'it':
            return 'it';
          case 'en':
            return 'en';
          default:
            return 'de';
        }
      }),
    );
  }

  private getGenderIconOptions$(): Observable<IconRadioButtonOption[]> {
    return this.translateService.onLangChange.pipe(
      untilDestroyed(this),
      startWith(''),
      withLatestFrom(this.store.select(createGetPersonOrUndefined(this.index))),
      map(([_, person]) => [
        {
          icon: 'hair-woman',
          label: this.translateService.instant('KEY_DATA.GENDER.FEMALE'),
          id: 'FEMALE',
          selected: person?.details.genderOrPrenatal === 'FEMALE',
        },
        {
          icon: 'hair-man',
          label: this.translateService.instant('KEY_DATA.GENDER.MALE'),
          id: 'MALE',
          selected: person?.details.genderOrPrenatal === 'MALE',
        },
        {
          icon: 'embryo',
          label: this.translateService.instant('KEY_DATA.GENDER.PRENATAL'),
          id: 'PRENATAL',
          selected: person?.details.genderOrPrenatal === 'PRENATAL',
        },
      ]),
    );
  }
}
